import { useCallback, useRef, useState } from "react";
import { useOutletContext } from "react-router-dom";
import { AlertTriangle } from "lucide-react";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { investingApi } from "../../services/api";
import { apiErrorMessage } from "../../services/apiError";
import { withoutBidiControls as plain } from "../snippets/bidi";
import AcknowledgeFillDialog from "./AcknowledgeFillDialog";
import { fillWhat, fillWhen, fillWhy, raisedAgain } from "./lib/fills";
import { asSentence } from "./lib/outcome";
import useRead from "./useRead";

// Stable for useRead: the banner's source (PR 4's fills slice) — its
// `count` is the banner's number, its rows say what and why.
const readUnacknowledged = () => investingApi.fills.list({ unacknowledged: "yes" });

/**
 * The red banner (walkthrough 10, spec §5.4) on Fund and Target: every
 * broker order that matches no order of a live sync plan of the current
 * target, until a person acknowledges it — what traded, when, why it's
 * flagged in plain words, and whether it was raised again (acknowledged,
 * then filled further). Nothing shows when there's nothing to flag; while
 * the first read is in flight it quietly says it's checking; a failed read
 * says so, since it can't say there's nothing.
 *
 * Acknowledge is a person's (spec §2.1): it fails closed while `me` is
 * unknown and is never offered to an agent session, which is told a person
 * does it. The confirm is an in-app dialog with an optional reason.
 */
function UnauthorizedBanner() {
  const { me } = useOutletContext() || {};
  const isPerson = me?.kind === "human";
  const isAgent = me?.kind === "agent";

  const [reloadTick, setReloadTick] = useState(0);
  const { data, loading, error, retry } = useRead(readUnacknowledged, reloadTick);
  const [acking, setAcking] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [done, setDone] = useState(null); // the fill just acknowledged
  const [uncertain, setUncertain] = useState(null); // {fill, message}: no answer came back
  // The acknowledged row (the opener) goes away: focus goes to what happened.
  const noteRef = useRef(null);
  const focusNoteRef = useRef(false);

  const rows = data?.results ?? [];
  const count = data?.count ?? 0;

  const openAck = (fill) => {
    setDone(null);
    setUncertain(null);
    setAcking(fill);
    setDialogOpen(true);
  };

  const onAcknowledged = useCallback((fill) => {
    focusNoteRef.current = true;
    setDialogOpen(false);
    setDone(fill);
    setReloadTick((tick) => tick + 1);
  }, []);

  const onUnknown = useCallback((what) => {
    focusNoteRef.current = true;
    setDialogOpen(false);
    setUncertain(what);
    setReloadTick((tick) => tick + 1);
  }, []);

  const onReload = useCallback(() => {
    setDialogOpen(false);
    setReloadTick((tick) => tick + 1);
  }, []);

  const onCloseAutoFocus = useCallback((event) => {
    if (!focusNoteRef.current) return;
    focusNoteRef.current = false;
    event.preventDefault();
    noteRef.current?.focus();
  }, []);

  let note = null;
  if (uncertain) {
    const { fill, message } = uncertain;
    note =
      `Couldn't tell whether the ${fill.side} of ${plain(fill.symbol)} (broker order ${plain(fill.broker_order_id)}) ` +
      `was acknowledged: ${asSentence(message)} It may or may not have gone through — the list was read again: ` +
      "if it's still listed, acknowledge it again.";
  } else if (done) {
    note = `Acknowledged the ${done.side} of ${plain(done.symbol)} (broker order ${plain(done.broker_order_id)}) — it's kept in the record.`;
    if (data && count === 0) note += " Every unauthorized order is acknowledged.";
  }

  let banner = null;
  if (error) {
    banner = (
      <Alert variant="destructive" role="alert">
        <AlertDescription className="flex flex-wrap items-center gap-2">
          <span>Couldn&apos;t check for unauthorized orders: {apiErrorMessage(error, "it didn't load.")}</span>{" "}
          <Button type="button" variant="outline" size="sm" className="h-11 md:h-8" onClick={retry}>
            Retry
          </Button>
        </AlertDescription>
      </Alert>
    );
  } else if (loading && !data) {
    // The first read: it can't say there's nothing yet, so it says it's looking.
    banner = <p className="m-0 text-sm text-muted-foreground">Checking for unauthorized orders…</p>;
  } else if (count > 0) {
    banner = (
      <section
        aria-labelledby="unauthorized-heading"
        className="rounded-lg border-2 border-destructive bg-destructive/5 p-4"
      >
        <h2 id="unauthorized-heading" className="m-0 flex items-center gap-2 text-lg font-semibold text-destructive">
          <AlertTriangle className="size-5 shrink-0" aria-hidden="true" />
          {count.toLocaleString("en-US")} unauthorized broker {count === 1 ? "order" : "orders"}
        </h2>
        <p className="mt-1 mb-0 text-sm">
          {count === 1 ? "It matches" : "Each matches"} no order of a live sync plan of the current target — Mono
          didn&apos;t ask for {count === 1 ? "it" : "them"}. Check Robinhood, then acknowledge{" "}
          {count === 1 ? "it" : "each one"} here; this stays until every one is acknowledged.
          {isAgent && " A person acknowledges these, in their own browser."}
        </p>
        {count > rows.length && (
          <p className="mt-1 mb-0 text-sm text-muted-foreground">
            Showing the newest {rows.length.toLocaleString("en-US")} of {count.toLocaleString("en-US")}.
          </p>
        )}
        <ul className="mt-3 mb-0 flex list-none flex-col gap-2 p-0">
          {rows.map((fill) => {
            const why = fillWhy(fill);
            return (
              <li key={fill.id} data-testid={`fill-${fill.id}`} className="rounded-md border bg-card p-3 text-sm">
                <div className="flex flex-col gap-2 sm:flex-row sm:items-start">
                  <div className="min-w-0 flex-1 wrap-anywhere">
                    <p className="m-0 font-semibold">{fillWhat(fill)}</p>
                    <p className="m-0 text-muted-foreground">
                      {fillWhen(fill)} · broker order{" "}
                      <span className="inline-block max-w-full">{plain(fill.broker_order_id)}</span>
                      {fill.created_by ? ` · reported by ${plain(fill.created_by.username)}` : ""}
                    </p>
                    {raisedAgain(fill) && (
                      <p className="mt-1 mb-0 font-semibold text-destructive">
                        Raised again: it was acknowledged, then filled further — the shares since are unauthorized
                        too.
                      </p>
                    )}
                    <p className="mt-1 mb-0">{why.reason}</p>
                    {why.detail && <p className="mt-1 mb-0 text-muted-foreground">{why.detail}</p>}
                  </div>
                  {isPerson && (
                    <Button
                      type="button"
                      variant="outline"
                      className="h-11 shrink-0 md:h-9"
                      onClick={() => openAck(fill)}
                    >
                      Acknowledge…
                    </Button>
                  )}
                </div>
              </li>
            );
          })}
        </ul>
      </section>
    );
  }

  return (
    <>
      <div
        ref={noteRef}
        role="status"
        aria-label="Acknowledged"
        tabIndex={-1}
        className={
          uncertain
            ? "mb-4 rounded-md border border-warning/60 bg-warning/10 p-3 text-sm outline-none"
            : note
              ? "mb-4 rounded-md border border-success/60 bg-success/10 p-3 text-sm outline-none"
              : "m-0"
        }
      >
        {note}
      </div>
      {banner && <div className="mb-4">{banner}</div>}
      {isPerson && acking && (
        <AcknowledgeFillDialog
          open={dialogOpen}
          fill={acking}
          onOpenChange={setDialogOpen}
          onAcknowledged={onAcknowledged}
          onReload={onReload}
          onUnknown={onUnknown}
          onCloseAutoFocus={onCloseAutoFocus}
        />
      )}
    </>
  );
}

export default UnauthorizedBanner;
